import React, { useEffect, useState } from "react";

import { Icon, IconSize } from "@gaming/xds-react-js";
import { Send } from "@gaming/xds-react-js-icons";

import {
  maxQuestionLength,
  supportAssistantStore,
  useSupportAssistant,
} from "../../helpers/supportAssistantStore";
import { useStrings } from "../../localization/localizationHelpers";
import * as styles from "./SupportAssistant.module.css";

type AssistantInputProps = Readonly<{
  focusOnMount?: boolean;
  /** Return false to stop the send (e.g. to confirm replacing a conversation). */
  onBeforeSend?: (text: string) => boolean;
}>;

export const AssistantInput: React.FC<AssistantInputProps> = ({ focusOnMount, onBeforeSend }) => {
  const { state, context } = useSupportAssistant();
  const [text, setText] = useState("");
  const [askLabel, placeholder, sendLabel, gameScopeLabel, allGames] = useStrings(
    "supportAssistant_askLabel",
    "supportAssistant_askPlaceholder",
    "supportAssistant_send",
    "supportAssistant_gameScopeLabel",
    "supportAssistant_allGames"
  );

  useEffect(() => {
    supportAssistantStore.loadHome();
  }, []);

  const games = state.home?.games ?? [];
  const canSend = !!text.trim() && !state.pending;

  const submit = () => {
    if (!canSend || (onBeforeSend && !onBeforeSend(text))) {
      return;
    }
    supportAssistantStore.send(text, context);
    setText("");
  };

  return (
    <form
      className={styles.inputBox}
      onSubmit={event => {
        event.preventDefault();
        submit();
      }}
    >
      <textarea
        className={styles.textarea}
        aria-label={askLabel}
        placeholder={placeholder}
        maxLength={maxQuestionLength}
        value={text}
        // eslint-disable-next-line jsx-a11y/no-autofocus -- the chat view exists to type a reply.
        autoFocus={focusOnMount}
        onChange={event => setText(event.target.value)}
        onKeyDown={event => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            submit();
          }
        }}
        data-testid="support-assistant-input"
      />
      <div className={styles.inputFooter}>
        {games.length > 0 ? (
          <select
            className={styles.select}
            aria-label={gameScopeLabel}
            value={state.gameScope?.id ?? ""}
            onChange={event =>
              supportAssistantStore.setGameScope(
                games.find(game => game.id === event.target.value) ?? null
              )
            }
          >
            <option value="">{allGames}</option>
            {games.map(game => (
              <option key={game.id} value={game.id}>
                {game.name}
              </option>
            ))}
          </select>
        ) : (
          <span />
        )}
        <button
          type="submit"
          className={styles.sendButton}
          aria-label={sendLabel}
          title={sendLabel}
          disabled={!canSend}
          data-testid="support-assistant-send"
        >
          <Icon aria-hidden="true" icon={Send} iconSize={IconSize.Custom} />
        </button>
      </div>
    </form>
  );
};
